/**
 * Import Latest Supplier Promise Dates Modal
 *
 * Download a pre-filled template, fill in the 'Latest Supplier Promise Date'
 * column (and Comment if uploading as a Supplier), then upload the file.
 *
 * Status state machine:
 *   Supplier:  new/accepted → change_proposed_supplier
 *   Customer:  new/accepted → change_proposed_supplier → accepted (auto-accept)
 */
import { useState } from 'react';
import { Loader2, Download, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { usePortalMode } from '../../../hooks/usePortalMode';
import FileDropZone from '../../common/FileDropZone';

/** CR-7066: one rejected row, numbered as it appears in the spreadsheet. */
interface ImportRowError {
  /** Spreadsheet row number — the header is row 1, so data starts at 2. */
  row: number;
  po_number: string;
  line: number | null;
  message: string;
}

interface ImportResult {
  total:   number;
  updated: number;
  ignored: number;
  errors:  number;
  /** Absent on responses from before per-row reporting existed. */
  row_errors?: ImportRowError[];
  /** Failures counted but not detailed, when there are more than the cap. */
  row_errors_truncated?: number;
}

interface ImportPromiseDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  /** Current view/filter params — template will match exactly what's in the table */
  exportParams?: Record<string, string>;
  /** Total count of lines matching current filters — shown in the instructions */
  totalCount?: number;
}

export default function ImportPromiseDatesModal({
  isOpen,
  onClose,
  onImportComplete,
  exportParams = {},
  totalCount,
}: ImportPromiseDatesModalProps) {
  const { isSupplier } = usePortalMode();

  const [downloading, setDownloading] = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result,  setResult]  = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);

  // ── Download Template ───────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.exportLines({
        ...exportParams,
        export_type: 'import_template',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `promise_dates_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  // ── Upload & Import ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await purchasingAPI.importPromiseDates(formData);
      const data = response.data;
      setResult(data);
      if (data.updated > 0) {
        toast.success(`${data.updated} line${data.updated !== 1 ? 's' : ''} updated`);
        setImportDone(true);
      } else {
        toast.info('No lines were updated');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; detail?: string } } };
      const msg =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Upload failed. Please check the file format.');
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  // ── Close / reset ───────────────────────────────────────────────────────────
  const handleClose = () => {
    // Refresh the table if import was successful
    if (importDone) {
      onImportComplete();
      setImportDone(false);
    }
    setSelectedFile(null);
    setResult(null);
    setErrorMsg(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Latest Supplier Promise Dates</DialogTitle>
          <DialogDescription>
            Download a pre-filled template containing your current PO lines, update the editable columns, then upload back.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">

          {/* Instructions */}
          <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm space-y-1">
            <p><span className="font-medium">Step 1:</span> Download the template — it will contain all {totalCount ? totalCount.toLocaleString() : ''} PO lines matching your current view and filters.</p>
            <p><span className="font-medium">Step 2:</span> Edit the yellow-highlighted columns: <span className="font-semibold">Latest Supplier Promise Date</span>, <span className="font-semibold">Proposed Unit Price</span>, <span className="font-semibold">Proposed Qty Ordered</span>, and <span className="font-semibold">Comment</span>.</p>
            <p><span className="font-medium">Step 3:</span> Upload the file — only rows with changes will be processed. Each change is logged in the line&apos;s change history.</p>
            <p className="text-muted-foreground mt-1">Only lines with status <span className="font-semibold">New</span>, <span className="font-semibold">WIP</span>, <span className="font-semibold">Changed</span>, or <span className="font-semibold">Change Declined</span> can be updated. Lines with other statuses (Declined, Dispatched, Cancelled) will be skipped.</p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="flex items-center gap-2"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Template
            </Button>

          </div>

          {/* Customer review 11 Sep (drag and drop everywhere). */}
          <FileDropZone
            size="compact"
            accept=".xlsx,.xls,.csv"
            disabled={uploading}
            hint="Excel (.xlsx, .xls) or CSV"
            onFiles={(files) => { setSelectedFile(files[0]); setResult(null); setErrorMsg(null); }}
            data-testid="import-promise-dates-drop-zone"
          />

          {/* Supplier hint: comment required */}
          {isSupplier && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
              If the promise date is changed, please ensure a reason is entered in the{' '}
              <span className="font-semibold">Comment</span> column. Rows without a comment will
              not be imported.
            </div>
          )}

          {/* Selected file */}
          {selectedFile && !uploading && !result && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <span className="font-medium truncate">{selectedFile.name}</span>
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <Button size="sm" onClick={handleUpload} disabled={uploading}>
                  {uploading
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Uploading…</>
                    : 'Import'}
                </Button>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-0.5 hover:bg-muted rounded"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* Uploading progress */}
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Parsing data, please wait…
            </div>
          )}

          {/* Error message */}
          {errorMsg && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Results table */}
          {result && (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-semibold">Import complete</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-2 font-medium">Count</th>
                    <th className="text-left px-4 py-2 font-medium">Updated</th>
                    <th className="text-left px-4 py-2 font-medium">Ignored</th>
                    {result.errors > 0 && (
                      <th className="text-left px-4 py-2 font-medium text-red-600">Errors</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3">{result.total}</td>
                    <td className="px-4 py-3 text-green-700 font-medium">{result.updated}</td>
                    <td className="px-4 py-3 text-muted-foreground">{result.ignored}</td>
                    {result.errors > 0 && (
                      <td className="px-4 py-3 text-red-600 font-medium">{result.errors}</td>
                    )}
                  </tr>
                </tbody>
              </table>
              {/* CR-7066: say which row failed and why, rather than a bare
                  count with a guessed-at explanation. */}
              {result.row_errors && result.row_errors.length > 0 && (
                <div className="border-t border-border">
                  <div className="px-4 py-2 text-xs font-medium text-red-700 bg-red-50">
                    These rows were not imported
                  </div>
                  <ul className="max-h-48 overflow-y-auto divide-y divide-border">
                    {result.row_errors.map((rowError, index) => (
                      <li key={`${rowError.row}-${index}`} className="px-4 py-2 text-xs">
                        <span className="font-medium">Row {rowError.row}</span>
                        {rowError.po_number && (
                          <span className="text-muted-foreground">
                            {' '}— PO {rowError.po_number}
                            {rowError.line !== null && ` line ${rowError.line}`}
                          </span>
                        )}
                        <div className="text-red-700 mt-0.5">{rowError.message}</div>
                      </li>
                    ))}
                  </ul>
                  {result.row_errors_truncated ? (
                    <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">
                      …and {result.row_errors_truncated} more row
                      {result.row_errors_truncated !== 1 ? 's' : ''} with errors.
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
